import { Sentry } from '@opendoor/observability';
import { ListenerEffectAPI } from '@reduxjs/toolkit';

import { AppDispatch, RootState } from 'src/store';
import { loadExperiment } from 'src/store/apiUtils';
import { ExperimentFlag } from 'src/store/experimentFlags';

import { FlowPage } from './FlowPage';
import { addPageLoader } from './loaders';

type FlowPageVariants = {
  control: FlowPage;
  [k: string]: FlowPage;
};

type ExperimentPage = Required<FlowPage>;

const getPage = (
  control: FlowPage,
  variants: Record<string, FlowPage>,
  bucket: string | null,
  flag: string,
): FlowPage => {
  if (bucket == null || bucket == 'control') {
    return control;
  }
  if (variants[bucket]) {
    return variants[bucket];
  }

  /* eslint-disable-next-line no-console */
  console.error(`Experiment variation: "${bucket}" not defined for flag: ${flag}`);
  Sentry.withScope((scope) => {
    scope.setExtras({ variant: bucket, experimentFlag: flag });
    Sentry.captureException(`Experiment variation: "${bucket}" not defined for flag: ${flag}`);
  });
  return control;
};

export const experimentPage = (
  flag: ExperimentFlag,
  { control, ...variants }: FlowPageVariants,
): ExperimentPage => ({
  isEnabled: (ctx) => {
    return getPage(control, variants, ctx.experimentFlags[flag], flag).isEnabled(ctx);
  },
  autoAdvance: (ctx) => {
    return getPage(control, variants, ctx.experimentFlags[flag], flag).autoAdvance(ctx);
  },
  carrot: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.carrot ? p.carrot(ctx) : undefined;
  },

  hideProgress: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.hideProgress ? p.hideProgress(ctx) : false;
  },

  image: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.image ? p.image(ctx) : undefined;
  },

  lastCarrot: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.lastCarrot ? p.lastCarrot(ctx) : false;
  },
  nextText: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.nextText ? p.nextText(ctx) : undefined;
  },
  note: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.note ? p.note(ctx) : undefined;
  },
  questions: (ctx) => {
    return getPage(control, variants, ctx.experimentFlags[flag], flag).questions(ctx);
  },
  showPrivacyNotice: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.showPrivacyNotice ? p.showPrivacyNotice(ctx) : false;
  },
  showSkipButton: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.showSkipButton ? p.showSkipButton(ctx) : false;
  },
  slug: control.slug,
  subtitle: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.subtitle ? p.subtitle(ctx) : undefined;
  },
  subtitleElement: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.subtitleElement ? p.subtitleElement(ctx) : undefined;
  },
  title: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.title ? p.title(ctx) : undefined;
  },
  tooltip: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.tooltip ? p.tooltip(ctx) : undefined;
  },
  exitPath: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.exitPath ? p.exitPath(ctx) : null;
  },
  secondaryAction: (ctx) => {
    const p = getPage(control, variants, ctx.experimentFlags[flag], flag);
    return p.secondaryAction ? p.secondaryAction(ctx) : undefined;
  },
});

export type IsEligibleForExperimentFn = (
  listenerApi: ListenerEffectAPI<RootState, AppDispatch>,
) => Promise<boolean> | boolean;

export const createExperimentPage = (
  flag: ExperimentFlag,
  variants: FlowPageVariants,
  isEligibleForExperiment?: IsEligibleForExperimentFn,
): ExperimentPage => {
  const newPage = experimentPage(flag, variants);

  const slug = newPage.slug();
  addPageLoader(slug, 'load-experiment', async (listenerApi) => {
    if (isEligibleForExperiment) {
      const isEligible = await isEligibleForExperiment(listenerApi);
      if (!isEligible) {
        return;
      }
    }

    // load the experiment and set auto impression to true
    await loadExperiment(listenerApi, flag, true);
  });
  return newPage;
};

/**
 * A helper to create disabled pages
 *
 * When setting up a test where the control page is disabled be sure the
 * slug you pass in to `dispabledPage` matches the control page's slug.
 */
export const disabledPage = (slug: string): FlowPage => ({
  slug: () => slug,
  isEnabled: () => false,
  autoAdvance: () => false,
  questions: () => [],
});
